import React, { PropsWithChildren, useEffect } from "react";

import ModalCloseIcon from "~/icons/ModalCloseIcon";

import styles from "./EmptyModal.module.css";
import clsx from "clsx";

type EmptyModalProps = {
  hideCloseButton?: boolean;
  visible: boolean;
  onClose: VoidFunction;
  modalClassName?: string;
};

const EmptyModal: React.FC<PropsWithChildren<EmptyModalProps>> = ({
  hideCloseButton = false,
  visible,
  onClose,
  modalClassName,
  children,
}) => {
  // Close on Escape while the modal is open
  useEffect(() => {
    if (!visible) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible, onClose]);

  return visible ? (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        // Only a click on the backdrop itself dismisses — not one bubbling up
        // from inside the dialog.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={clsx(styles.modal, modalClassName)}
        role="dialog"
        aria-modal="true"
      >
        {!hideCloseButton && (
          <div
            className={styles.closeButton}
            onClick={onClose}
            role="button"
            tabIndex={0}
            aria-label="Close"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClose();
              }
            }}
          >
            <ModalCloseIcon />
          </div>
        )}
        {children}
      </div>
    </div>
  ) : null;
};

export default EmptyModal;
